/**
 * Reminder Modal Component
 *
 * Full-screen modal for setting screening reminders.
 * Can be triggered from:
 *   - React components via useReminder().openReminder({ screeningId, filmTitle, screeningTime })
 *   - Vanilla JS via window.rialtoOpenReminderModal({ screeningId, filmTitle, screeningTime })
 *   - Server-rendered .rialto-remind-bell buttons (auto-detected via delegated click)
 *
 * Flow:
 *  1. User taps bell → modal opens with time options
 *  2. User picks a time
 *     a. If authenticated → POST alert immediately
 *     b. If not → PhoneAuthModal opens, then auto-submits after auth
 */

import { useState, useEffect, useCallback } from '@wordpress/element';
import { useAuth } from '../context/AuthContext';
import { useReminder } from '../context/ReminderContext';
import PhoneAuthModal from './PhoneAuthModal';

export default function ReminderModal() {
    const { isAuthenticated, getAuthHeader, clearAuth } = useAuth();
    const { reminderData, closeReminder } = useReminder();
    const [step, setStep] = useState('options'); // 'options' | 'loading' | 'success' | 'error' | 'auth'
    const [selectedMinutes, setSelectedMinutes] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [afterHoursWarning, setAfterHoursWarning] = useState(false);
    const [overwriteMessage, setOverwriteMessage] = useState('');

    const t = window.rialtoData?.translations || {};

    // Reset state when new reminder data arrives
    useEffect(() => {
        if (reminderData) {
            setStep('options');
            setSelectedMinutes(null);
            setErrorMessage('');
            setAfterHoursWarning(false);
            setOverwriteMessage('');
            document.body.style.overflow = 'hidden';
        }
    }, [reminderData]);

    // Attach delegated click handler for server-rendered bell buttons
    useEffect(() => {
        const handleBellClick = (e) => {
            const btn = e.target.closest('.rialto-remind-bell');
            if (!btn) return;

            e.preventDefault();
            e.stopPropagation();

            const dt = new Date(btn.dataset.screeningTime);
            if (dt <= new Date()) return; // Don't open for past screenings

            const formatted = dt.toLocaleDateString('en-NZ', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                hour: 'numeric',
                minute: '2-digit',
            });

            window.rialtoOpenReminderModal({
                screeningId: btn.dataset.screeningId,
                filmTitle: btn.dataset.filmTitle,
                screeningTime: formatted,
                screeningDatetime: btn.dataset.screeningTime,
            });
        };

        document.addEventListener('click', handleBellClick);
        return () => document.removeEventListener('click', handleBellClick);
    }, []);

    const close = useCallback(() => {
        closeReminder();
        document.body.style.overflow = '';
    }, [closeReminder]);

    // Handle ESC key
    useEffect(() => {
        if (!reminderData) return;
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [reminderData, close]);

    const submitAlert = useCallback(async (minutes, token) => {
        setStep('loading');
        try {
            const headers = token
                ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                : { ...getAuthHeader(), 'Content-Type': 'application/json' };

            const response = await fetch('/wp-json/rialto/v1/alerts', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    screening_id: parseInt(reminderData.screeningId, 10),
                    reminder_minutes: parseInt(minutes, 10),
                }),
            });

            // Handle 401/403 — token expired or invalid, re-auth needed
            if (response.status === 401 || response.status === 403) {
                clearAuth();
                setSelectedMinutes(minutes);
                setStep('auth');
                return;
            }

            const data = await response.json();

            if (data.success || data.alert_id) {
                setSelectedMinutes(minutes);
                setAfterHoursWarning(!!data.after_hours_warning);
                setOverwriteMessage(data.overwrite_message || '');
                setStep('success');
            } else if (data.code === 'already_exists') {
                setErrorMessage(data.message || 'You already have this reminder set.');
                setStep('error');
            } else if (data.code === 'rest_forbidden' || data.code === 'not_authenticated') {
                // Also catch auth errors returned in JSON body
                clearAuth();
                setSelectedMinutes(minutes);
                setStep('auth');
            } else {
                setErrorMessage(data.message || 'Failed to set reminder.');
                setStep('error');
            }
        } catch (err) {
            setErrorMessage(err.message || 'An error occurred. Please try again.');
            setStep('error');
        }
    }, [reminderData, getAuthHeader, clearAuth]);

    const handleOptionClick = useCallback((minutes) => {
        if (!isAuthenticated) {
            setSelectedMinutes(minutes);
            setStep('auth');
            return;
        }
        submitAlert(minutes);
    }, [isAuthenticated, submitAlert]);

    const handleAuthSuccess = useCallback(() => {
        // Auth complete — submit the pending reminder with fresh token
        const token = localStorage.getItem('rialto_token');
        if (token && selectedMinutes) {
            submitAlert(selectedMinutes, token);
        } else {
            setStep('options');
        }
    }, [selectedMinutes, submitAlert]);

    const handleAuthClose = useCallback(() => {
        setStep('options');
    }, []);

    const getTimeLabel = (mins) => {
        if (mins >= 60) {
            const hours = mins / 60;
            return hours + ' hour' + (hours > 1 ? 's' : '');
        }
        return mins + ' minutes';
    };

    if (!reminderData) return null;

    // When in auth step, render PhoneAuthModal as full overlay (not nested)
    if (step === 'auth') {
        return (
            <PhoneAuthModal
                onClose={handleAuthClose}
                onSuccess={handleAuthSuccess}
            />
        );
    }

    return (
        <div className="rialto-modal-overlay active" onClick={close}>
            <div className="rialto-modal rialto-modal-small" onClick={(e) => e.stopPropagation()}>
                <button className="rialto-modal-close" onClick={close}>&times;</button>

                {step === 'options' && (
                    <div className="rialto-reminder-content">
                        <div className="rialto-reminder-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                        </div>
                        <h3 className="rialto-reminder-title">
                            Set Reminder
                        </h3>
                        <p className="rialto-reminder-subtitle">
                            {reminderData.filmTitle}
                            {reminderData.screeningTime ? ` - ${reminderData.screeningTime}` : ''}
                        </p>
                        <p className="rialto-reminder-note">
                            How long before the screening would you like to be reminded?
                        </p>
                        <div className="rialto-reminder-options">
                            {[
                                { mins: 180, label: '3 hours' },
                                { mins: 60,  label: '1 hour' },
                                { mins: 30,  label: '30 min' },
                                { mins: 10,  label: '10 min' },
                            ].filter(opt => {
                                if (!reminderData.screeningDatetime) return true;
                                const screeningMs = new Date(reminderData.screeningDatetime).getTime();
                                const reminderMs = screeningMs - opt.mins * 60 * 1000;
                                return reminderMs > Date.now();
                            }).map(opt => (
                                <button key={opt.mins} className="rialto-reminder-option" onClick={() => handleOptionClick(opt.mins)}>
                                    <span className="option-time">{opt.label}</span>
                                    <span className="option-desc">before</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {step === 'loading' && (
                    <div className="rialto-reminder-content">
                        <div className="rialto-reminder-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="rialto-spin">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                        </div>
                        <h3 className="rialto-reminder-title">Setting reminder...</h3>
                    </div>
                )}

                {step === 'success' && (
                    <div className="rialto-reminder-content">
                        <div className="rialto-reminder-success">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#46d369" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M9 12l2 2 4-4"/>
                            </svg>
                            <h3>{t.reminderSet || 'Reminder Set!'}</h3>
                            <p>We'll remind you {getTimeLabel(selectedMinutes)} before the screening.</p>
                            {overwriteMessage && (
                                <p className="rialto-alert-warning">{overwriteMessage}</p>
                            )}
                            {afterHoursWarning && (
                                <p className="rialto-alert-warning">
                                    {t.afterHoursWarning || 'This reminder may arrive during nighttime hours'}
                                </p>
                            )}
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '8px' }}>
                                <button className="rialto-btn rialto-btn-primary" onClick={close}>
                                    Done
                                </button>
                                <button className="rialto-btn rialto-btn-secondary" onClick={() => { setStep('options'); setOverwriteMessage(''); }}>
                                    Add Another
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'error' && (
                    <div className="rialto-reminder-content">
                        <div className="rialto-reminder-success">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e50914" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <h3>Oops</h3>
                            <p>{errorMessage}</p>
                            <button className="rialto-btn rialto-btn-primary" onClick={() => setStep('options')}>
                                {t.retry || 'Try Again'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
